import { useTranslation } from "react-i18next";import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { tenancyAPI } from '../services/api';

const TenantContext = createContext(null);
const SCOPE_SELECTION_STORAGE_KEY = 'ezerp_scope_selection';

const normalizeIdList = (value) => {
  if (!Array.isArray(value)) return [];
  return Array.from(new Set(value.map(item => Number(item)).filter(Number.isFinite)));
};

const buildDefaultSelection = (context) => ({
  mode: 'single',
  selectedClientIds: context?.client_id && !context?.business_id ? [Number(context.client_id)] : [],
  selectedBusinessIds: context?.business_id ? [Number(context.business_id)] : [],
  includeAllAuthorized: false
});

const readStoredSelection = () => {
  try {
    const raw = localStorage.getItem(SCOPE_SELECTION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return {
      mode: parsed?.includeAllAuthorized ? 'all_authorized' : (parsed?.mode || 'multi'),
      selectedClientIds: normalizeIdList(parsed?.selectedClientIds),
      selectedBusinessIds: normalizeIdList(parsed?.selectedBusinessIds),
      includeAllAuthorized: Boolean(parsed?.includeAllAuthorized)
    };
  } catch (error) {
    localStorage.removeItem(SCOPE_SELECTION_STORAGE_KEY);
    return null;
  }
};

const writeStoredSelection = (selection) => {
  localStorage.setItem(SCOPE_SELECTION_STORAGE_KEY, JSON.stringify(selection));
};

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};

export const TenantProvider = ({ children }) => {const { t } = useTranslation("common");
  const { isAuthenticated, token } = useAuth();
  const [availableScopes, setAvailableScopes] = useState({ tenants: [] });
  const [activeContext, setActiveContext] = useState(null);
  const [selectedScopes, setSelectedScopes] = useState(() => readStoredSelection() || {
    mode: 'single',
    selectedClientIds: [],
    selectedBusinessIds: [],
    includeAllAuthorized: false
  });
  const [loading, setLoading] = useState(true);
  const [showSelector, setShowSelector] = useState(false);

  // 1. Load Available Scopes & Active Context
  const loadContext = useCallback(async () => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    try {
      const [scopes, current] = await Promise.all([
      tenancyAPI.getAvailableScopes(),
      tenancyAPI.getCurrentContext()]
      );

      setAvailableScopes(scopes);
      setActiveContext(current);

      const storedSelection = readStoredSelection();
      const nextSelection = storedSelection || buildDefaultSelection(current);
      setSelectedScopes(nextSelection);
      writeStoredSelection(nextSelection);

      // Store effective context IDs if not set
      if (!localStorage.getItem('ezerp_tenant_id') && current.client_id) {
        localStorage.setItem('ezerp_tenant_id', current.client_id.toString());
      }
      if (!localStorage.getItem('ezerp_org_id') && current.business_id) {
        localStorage.setItem('ezerp_org_id', current.business_id.toString());
      }

      // Proactive: If user has multiple options and hasn't picked, show selector
      const storedOrgId = localStorage.getItem('ezerp_org_id');
      if (isAuthenticated && !storedOrgId) {
        // Only show if there's actually something to select
        if (scopes.tenants.length > 0) {
          setShowSelector(true);
        }
      }
    } catch (error) {
      console.error('Failed to load tenancy context:', error);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  // 2. Context Switching (Phase 2 Design: Full Reload)
  const switchContext = (tenantId, orgId, redirectTo = null) => {
    if (tenantId) localStorage.setItem('ezerp_tenant_id', tenantId.toString());
    if (orgId) localStorage.setItem('ezerp_org_id', orgId.toString());else
    localStorage.removeItem('ezerp_org_id'); // If switching to tenant-only scope

    // Force full app reload to ensure clean cache and consistent context injection
    if (redirectTo) {
      window.location.href = redirectTo;
    } else {
      window.location.reload();
    }
  };

  const saveSelectedScopes = useCallback((nextSelection) => {
    const normalized = {
      mode: nextSelection.includeAllAuthorized ? 'all_authorized' : (
        normalizeIdList(nextSelection.selectedClientIds).length + normalizeIdList(nextSelection.selectedBusinessIds).length > 1
          ? 'multi'
          : 'single'
      ),
      selectedClientIds: normalizeIdList(nextSelection.selectedClientIds),
      selectedBusinessIds: normalizeIdList(nextSelection.selectedBusinessIds),
      includeAllAuthorized: Boolean(nextSelection.includeAllAuthorized)
    };
    setSelectedScopes(normalized);
    writeStoredSelection(normalized);
    return normalized;
  }, []);

  const toggleTenantSelection = useCallback((tenantId) => {
    const id = Number(tenantId);
    if (!Number.isFinite(id)) return;
    saveSelectedScopes({
      ...selectedScopes,
      includeAllAuthorized: false,
      selectedClientIds: selectedScopes.selectedClientIds.includes(id)
        ? selectedScopes.selectedClientIds.filter(item => item !== id)
        : [...selectedScopes.selectedClientIds, id]
    });
  }, [saveSelectedScopes, selectedScopes]);

  const toggleBusinessSelection = useCallback((businessId) => {
    const id = Number(businessId);
    if (!Number.isFinite(id)) return;
    saveSelectedScopes({
      ...selectedScopes,
      includeAllAuthorized: false,
      selectedBusinessIds: selectedScopes.selectedBusinessIds.includes(id)
        ? selectedScopes.selectedBusinessIds.filter(item => item !== id)
        : [...selectedScopes.selectedBusinessIds, id]
    });
  }, [saveSelectedScopes, selectedScopes]);

  const selectAllAuthorizedScopes = useCallback(() => {
    const allClientIds = normalizeIdList(availableScopes?.tenants?.map(tenant => tenant.id));
    const allBusinessIds = normalizeIdList(
      (availableScopes?.tenants || []).flatMap(tenant => tenant.org_nodes?.map(org => org.id) || [])
    );
    saveSelectedScopes({
      mode: 'all_authorized',
      selectedClientIds: allClientIds,
      selectedBusinessIds: allBusinessIds,
      includeAllAuthorized: true
    });
  }, [availableScopes, saveSelectedScopes]);

  const clearSelectedScopes = useCallback(() => {
    const fallback = buildDefaultSelection(activeContext);
    saveSelectedScopes(fallback);
  }, [activeContext, saveSelectedScopes]);

  const selectedScopePayload = useMemo(() => ({
    mode: selectedScopes.includeAllAuthorized ? 'all_authorized' : selectedScopes.mode,
    selectedClientIds: selectedScopes.selectedClientIds,
    selectedBusinessIds: selectedScopes.selectedBusinessIds,
    includeAllAuthorized: selectedScopes.includeAllAuthorized
  }), [selectedScopes]);

  const selectedScopeSummary = useMemo(() => {
    const selectedNames = [];
    const clientIds = new Set(selectedScopes.selectedClientIds);
    const businessIds = new Set(selectedScopes.selectedBusinessIds);
    for (const tenant of availableScopes?.tenants || []) {
      if (clientIds.has(Number(tenant.id))) selectedNames.push(tenant.name);
      for (const org of tenant.org_nodes || []) {
        if (businessIds.has(Number(org.id))) selectedNames.push(`${tenant.name} / ${org.name}`);
      }
    }
    if (selectedScopes.includeAllAuthorized) return `All authorized scopes (${selectedNames.length})`;
    if (selectedNames.length === 0) return activeContext?.client_name || 'Select Tenant';
    if (selectedNames.length === 1) return selectedNames[0];
    return `${selectedNames.length} scopes selected`;
  }, [activeContext, availableScopes, selectedScopes]);

  useEffect(() => {
    if (isAuthenticated) {
      loadContext();
    } else {
      setAvailableScopes({ tenants: [] });
      setActiveContext(null);
      setLoading(false);
    }
  }, [isAuthenticated, loadContext]);

  const value = {
    availableScopes,
    activeContext,
    selectedScopes,
    selectedScopePayload,
    selectedScopeSummary,
    loading,
    showSelector,
    setShowSelector,
    switchContext,
    toggleTenantSelection,
    toggleBusinessSelection,
    selectAllAuthorizedScopes,
    clearSelectedScopes,
    refreshContext: loadContext,

    // Legacy mapping for Phase 1 compatibility
    businesses: activeContext?.allowed_business_ids || [],
    currentBusiness: activeContext?.business_id ?
    { id: activeContext.business_id, name: activeContext.business_name } :
    null,

    // Access checks
    isPlatformAdmin: activeContext?.is_platform_user || false,
    allowedModules: activeContext?.allowed_modules || [],
    canAccessModule: (code) => activeContext?.is_platform_user || activeContext?.allowed_modules?.includes(code)
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>);

};

export default TenantContext;
